import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import { leaveMeeting } from "../../hooks/useFirestore";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useApprovalNotifications } from "../../context/ApprovalNotificationContext";

import { useTask } from "../../context/TaskContext";
import { HR_NAV, ADMIN_NAV, LEAD_NAV, EMPLOYEE_NAV } from "../../utils/navigation";

let globalSidebarScrollPos = 0;
try {
  const saved = sessionStorage.getItem("sidebar_scroll_pos");
  if (saved) globalSidebarScrollPos = parseInt(saved, 10) || 0;
} catch (e) {}

function getInitials(name = "") {
  return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

function getAvatarColor(name = "") {
  const colors = ["#4f46e5","#0ea5e9","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899"];
  let hash = 0;
  for (let c of name) hash = c.charCodeAt(0) + hash * 31;
  return colors[Math.abs(hash) % colors.length];
}

export default function Layout({ navItems, panelLabel, children }) {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { notifications, unreadCount: notificationUnreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { counts } = useApprovalNotifications();
  const { unreadTaskCount } = useTask();
  const [announcementUnreadCount, setAnnouncementUnreadCount] = useState(0);

  const isHRRoute = location.pathname.startsWith("/hr");
  const isHR = userProfile?.role === "hr" || isHRRoute;

  const currentNavItems = isHR
    ? HR_NAV
    : navItems ||
      (userProfile?.role === "admin"
        ? ADMIN_NAV
        : userProfile?.role === "lead"
        ? LEAD_NAV
        : EMPLOYEE_NAV);

  const currentPanelLabel = isHR
    ? "HR Panel"
    : panelLabel ||
      (userProfile?.role === "admin"
        ? "Admin Panel"
        : userProfile?.role === "lead"
        ? "Team Lead Panel"
        : "Employee Panel");

  useLayoutEffect(() => {
    if (navRef.current) {
      navRef.current.scrollTop = globalSidebarScrollPos;
    }
  }, [location.pathname]);

  const handleNavScroll = (e) => {
    globalSidebarScrollPos = e.target.scrollTop;
    try {
      sessionStorage.setItem("sidebar_scroll_pos", String(e.target.scrollTop));
    } catch (err) {}
  };

  useEffect(() => {
    if (userProfile?.role !== "employee" || !userProfile?.teamId) {
      setAnnouncementUnreadCount(0);
      return;
    }

    const q = query(
      collection(db, "announcements"),
      
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const uid = userProfile.uid;
        const unread = snap.docs.filter((docSnap) => {
  const data = docSnap.data();

  const isVisible =
    data.audience === "all" ||
    data.teamId === userProfile.teamId;

  return (
    isVisible &&
    !(data.readBy || []).includes(uid)
  );
});
        setAnnouncementUnreadCount(unread.length);
      },
      (err) => {
        console.error("Error watching announcements in Layout:", err);
      }
    );

    const navigationWithBadges = navItems.map((section) => ({
  ...section,
  items: section.items.map((item) => {
    if (item.label === "Leave Requests") {
      return {
        ...item,
        badge: counts.leave > 0 ? counts.leave : null,
      };
    }

    return item;
  }),
}));

    return () => unsub();
  }, [userProfile?.role, userProfile?.teamId, userProfile?.uid]);

  async function handleLogout() {
    // If user is in a meeting, clear status on logout
    if (userProfile?.inMeeting) {
      await leaveMeeting(userProfile.uid);
    }
    await logout();
    navigate("/login", { replace: true });
  }

  async function handleLeaveMeeting() {
    await leaveMeeting(userProfile.uid);
  }

  const avatarBg = getAvatarColor(userProfile?.displayName || "");
  const initials  = getInitials(userProfile?.displayName);
  const isInMeeting = userProfile?.inMeeting === true;

  const navigationWithBadges = navItems.map((section) => ({
  ...section,
  items: section.items.map((item) => {
    if (item.label === "Leave Requests") {
      return {
        ...item,
        badge: counts.leave > 0 ? counts.leave : null,
      };
    }

    return item;
  }),
}));

  return (
    <div className={`layout ${collapsed ? "layout--collapsed" : ""}`}>
      {mobileOpen && (
        <div className="layout-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* ── Sidebar ─────────────────────────────── */}
      <aside className={`sidebar ${mobileOpen ? "sidebar--open" : ""}`}>
        {/* Branding */}
        <div className="sidebar-brand">
          <div className="sidebar-logo">
  <img
    src="/logo.png"
    alt="Logo"
    className="sidebar-logo-img"
  />
</div>
          {!collapsed && (
            <div className="sidebar-brand-text">
              <span className="sidebar-brand-name">Samriddhi</span>
              <span className="sidebar-brand-sub">Anveshana</span>
            </div>
          )}
          <button
            className="sidebar-collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? "▶" : "◀"}
          </button>
        </div>

        {/* Panel label */}
        {!collapsed && (
          <div className="sidebar-panel-label">{currentPanelLabel}</div>
        )}

        {/* Navigation */}
        <nav className="sidebar-nav" ref={navRef} onScroll={handleNavScroll}>
          {currentNavItems.map((section) => (
            <div key={section.section} className="sidebar-section">
              {!collapsed && (
                <span className="sidebar-section-label">{section.section}</span>
              )}
              {section.items.map((item) => {
                const hasUnreadAnnouncements = item.label === "Announcements" && announcementUnreadCount > 0;

                const hasUnreadTasks =
  item.label === "Tasks" &&
  unreadTaskCount > 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `sidebar-nav-item ${isActive ? "sidebar-nav-item--active" : ""}`
                    }
                    title={collapsed ? item.label : ""}
                    onClick={() => setMobileOpen(false)}
                  >
                    <span className="sidebar-nav-icon" style={{ position: "relative" }}>
                      {item.icon}
                      {hasUnreadAnnouncements && (
                        <span
                          style={{
                            position: "absolute",
                            top: "-2px",
                            right: "-2px",
                            width: "8px",
                            height: "8px",
                            backgroundColor: "#ef4444",
                            borderRadius: "50%",
                            border: "1px solid var(--sidebar-bg)",
                          }}
                        />
                      )}
                    </span>
                    {!collapsed && (
                      <span className="sidebar-nav-label" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {item.label}
                        {hasUnreadAnnouncements && (
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              backgroundColor: "#ef4444",
                              borderRadius: "50%",
                              display: "inline-block"
                            }}
                          />
                        )}
                      </span>
                    )}
                    {!collapsed && (
  <>
    {item.badge ? (
      <span className="sidebar-badge">
        {item.badge}
      </span>
    ) : hasUnreadTasks ? (
      <span className="sidebar-badge">
        {unreadTaskCount}
      </span>
    ) : null}
  </>
)}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* In-meeting banner (collapsed: icon only) */}
        {isInMeeting && (
          <div className={`sidebar-meeting-banner ${collapsed ? "sidebar-meeting-banner--collapsed" : ""}`}>
            {!collapsed ? (
              <>
                <span className="sidebar-meeting-pulse" />
                <span className="sidebar-meeting-text">In Meeting</span>
                <button
                  className="sidebar-meeting-leave"
                  onClick={handleLeaveMeeting}
                  title="Leave meeting"
                >
                  ✕
                </button>
              </>
            ) : (
              <span
                className="sidebar-meeting-pulse"
                title="In Meeting — click to leave"
                onClick={handleLeaveMeeting}
                style={{ cursor: "pointer" }}
              />
            )}
          </div>
        )}

        {/* User profile + logout */}
        <div className="sidebar-user">
          {/* Avatar with green dot if in meeting */}
          <div className="sidebar-avatar-wrap">
            <div
              className="sidebar-avatar"
              style={{ backgroundColor: avatarBg }}
            >
              {initials}
            </div>
            {isInMeeting && <span className="sidebar-meeting-dot" />}
          </div>

          {!collapsed && (
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">
                {userProfile?.displayName || "Loading…"}
              </span>
              <span className="sidebar-user-id">
                {isInMeeting
                  ? <span className="sidebar-inmeet-label">● In Meeting</span>
                  : userProfile?.employeeId || ""}
              </span>
            </div>
          )}
          <button
            className="sidebar-logout-btn"
            onClick={handleLogout}
            title="Sign out"
          >
            ⏻
          </button>
        </div>
      </aside>

      {/* ── Main content ────────────────────────── */}
      <div className="layout-main">
        <header className="layout-topbar">
          <button
            className="topbar-menu-btn"
            onClick={() => setMobileOpen(true)}
          >
            ☰
          </button>
          <span className="topbar-title">{panelLabel}</span>
          
          <div className="topbar-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginLeft: 'auto' }}>
            <div className="notification-wrapper" style={{ position: 'relative' }}>
              <button 
                className="notification-btn" 
                onClick={() => setShowNotifications(!showNotifications)}

  style={{
    background: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: "50%",
    cursor: "pointer",
    position: "relative",
    padding: "8px",
    color: "#111827",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>              
<svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    fill="none"
    viewBox="0 0 24 24"
    stroke="#111827"
    strokeWidth="2"
>                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                </svg>
                {notificationUnreadCount > 0 && (
                  <span style={{ position: 'absolute', top: 0, right: 0, background: 'red', color: 'white', borderRadius: '50%', padding: '0.1rem 0.4rem', fontSize: '0.7rem', fontWeight: 'bold' }}>
                    {notificationUnreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="notification-dropdown" style={{ position: 'absolute', right: 0, top: '100%', width: '300px', background: 'white', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', borderRadius: '0.5rem', zIndex: 50, border: '1px solid #e5e7eb', maxHeight: '400px', overflowY: 'auto' }}>
                  <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Notifications</h3>
                    {notificationUnreadCount > 0 && (
                      <button onClick={markAllAsRead} style={{ fontSize: '0.8rem', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}>
                        Mark all as read
                      </button>
                    )}
                  </div>
                  <div style={{ padding: '0.5rem' }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }}>No notifications</div>
                    ) : (
                      notifications.map(n => {
                        const isUnread = !n.readBy?.includes(userProfile?.uid);
                        return (
                          <div 
                            key={n.id} 


                            onClick={async () => {
  await markAsRead(n.id);

  switch (n.type) {
    case "meeting":
      navigate(
        userProfile?.role === "admin"
          ? "/admin/meetings"
          : userProfile?.role === "lead"
          ? "/lead/meetings"
          : "/employee/meetings"
      );
      break;

    case "announcement":
      navigate(
        userProfile?.role === "admin"
          ? "/admin/announcements"
          : userProfile?.role === "lead"
          ? "/lead/announcements"
          : "/employee/announcements"
      );
      break;

   case "query":
  navigate(
    userProfile?.role === "admin"
      ? "/admin/queries"
      : userProfile?.role === "lead"
      ? "/lead/queries"
      : "/employee/queries"
  );
  break;
    

    case "task":
      navigate(
        userProfile?.role === "admin"
          ? "/admin/tasks"
          : userProfile?.role === "lead"
          ? "/lead/tasks"
          : "/employee/tasks"
      );
      break;

    default:
      if (n.actionUrl) {
        navigate(n.actionUrl);
      }
      break;
  }

  setShowNotifications(false);
}}
                            style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: isUnread ? '#eff6ff' : 'transparent', borderRadius: '0.25rem', marginBottom: '0.25rem' }}
                          >
                            <div style={{ fontSize: '0.9rem', fontWeight: isUnread ? 600 : 500, color: '#111827' }}>{n.title}</div>
                            <div style={{ fontSize: '0.8rem', color: '#4b5563', marginTop: '0.25rem' }}>{n.message}</div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="topbar-avatar-wrap">
              <div
                className="topbar-avatar"
                style={{ backgroundColor: avatarBg }}
              >
                {initials}
              </div>
              {isInMeeting && <span className="topbar-meeting-dot" />}
            </div>
          </div>
        </header>

        <main className="layout-content">{children}</main>
      </div>
    </div>
  );
}